import React, { useState, useEffect, type FormEvent, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Checkbox, Flex, Text, useSarah } from "@unifize/sarah";
import * as R from "ramda";

import {
  getCurrentChecklistBuilderFields,
  getChecklistFieldsById,
  getLlmModels as getLlmModelsSelector,
} from "src/reducers";
import { getLlmModels } from "src/actions/llm";
import PromptInput from "./PromptInput";
import {
  TextInput,
  MultiSelect,
  SingleSelect,
  TextArea,
} from "src/components/Unifize";
import { Label } from "src/components/Unifize/Input/TextInput/styles";
import {
  aiExcludedInputFieldTypes,
  aiExcludedOutputFieldTypes,
} from "src/constants/checklist";
import Icon from "src/icons";
import type {
  AiFieldSettings,
  AppState,
  ChecklistField,
  FieldId,
} from "src/types";
import { LlmModel, LlmModelOption } from "src/ts-types";

type Props = {
  settings: AiFieldSettings;
  saveSettings: Function;
};

const Ai = ({ settings, saveSettings }: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { colors } = useSarah();
  const currentChecklistFieldLabel = useSelector(
    ({ app }: { app: AppState }) =>
      app.workflow.builderDialog.checklistFieldSettings.label,
  );
  const fieldsById = useSelector(({ app }: { app: AppState }) =>
    getChecklistFieldsById(app),
  );
  const dataAI: Array<LlmModel> = useSelector(({ app }: { app: AppState }) =>
    getLlmModelsSelector(app),
  );

  const [currentSettings, setCurrentSettings] = useState<AiFieldSettings>({
    placeholder: "",
    prompt: "",
    files: [],
    inputFields: [],
    outputFields: [],
    autoFill: false,
    ai: "",
    processInstructions: "",
  });

  useEffect(() => {
    if (settings) {
      const {
        placeholder,
        prompt,
        inputFields,
        outputFields,
        files = [],
        autoFill,
        ai = "",
        model,
        processInstructions = "",
      } = settings;

      // Convert model (number) to ai (string) if ai is not present
      const aiValue = ai || (model != null ? model.toString() : "");

      setCurrentSettings({
        placeholder,
        prompt,
        files,
        inputFields,
        outputFields,
        autoFill,
        ai: aiValue,
        processInstructions: processInstructions || "",
      });
    }
  }, [settings]);

  useEffect(() => {
    saveSettings(currentSettings);
  }, [currentSettings]);

  useEffect(() => {
    if (dataAI.length === 0) {
      dispatch(getLlmModels());
    }
  }, [dispatch, dataAI.length]);

  const checklistFields = useSelector(({ app }: { app: AppState }) =>
    R.reject(
      (field) => field.label === currentChecklistFieldLabel,
      getCurrentChecklistBuilderFields(app),
    ),
  );

  const handlePlaceholder = (e: KeyboardEvent) => {
    const value = (e.target as HTMLInputElement)?.value;
    setCurrentSettings((prev) => ({ ...prev, placeholder: value }));
  };

  const handlePrompt = (e: FormEvent<HTMLTextAreaElement>) => {
    const value = (e.target as HTMLInputElement)?.value;
    setCurrentSettings((prev) => ({ ...prev, prompt: value }));
  };

  const handleFile = (file: string) => {
    setCurrentSettings((prev) => {
      const alreadySelected = prev.files.includes(file);

      if (alreadySelected) {
        const updatedFiles = prev.files.filter((f) => f !== file);
        return { ...prev, files: updatedFiles };
      } else {
        const updatedFiles = [...prev.files, file];
        return { ...prev, files: updatedFiles };
      }
    });
  };

  const handleSelect = (
    key: "inputFields" | "outputFields",
    value: Array<FieldId>,
  ) => {
    setCurrentSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSelectAI = (value: string) => {
    setCurrentSettings((prev) => ({
      ...prev,
      ai: value,
    }));
  };

  const {
    placeholder,
    prompt,
    inputFields,
    outputFields,
    files,
    autoFill,
    ai,
    processInstructions,
  } = currentSettings;

  const inputFieldOptions = React.useMemo(() => {
    return R.reject(
      (field) =>
        aiExcludedInputFieldTypes.includes(field.type) ||
        // $FlowFixMe - Optional chaining
        (field.id !== undefined && outputFields?.includes(field.id)),
      checklistFields,
    );
  }, [checklistFields, outputFields]);

  const outputFieldOptions = React.useMemo(() => {
    return R.reject(
      (field) =>
        aiExcludedOutputFieldTypes.includes(field.type) ||
        (field.id !== undefined && inputFields?.includes(field.id)) ||
        (autoFill && field.type === "link"),
      checklistFields,
    );
  }, [checklistFields, inputFields, autoFill]);

  const handleToggleAutoFill = (): void => {
    if (autoFill) {
      setCurrentSettings((prev) => ({ ...prev, autoFill: !prev.autoFill }));
    } else {
      // Remove linked fields from output fields
      const filteredOutputFields = outputFields.filter((fieldId) => {
        const field: ChecklistField = fieldsById.get(fieldId.toString());
        if (field) {
          return field.get("type") !== "link";
        }
        return false;
      });

      setCurrentSettings((prev) => ({
        ...prev,
        autoFill: !prev.autoFill,
        outputFields: filteredOutputFields,
      }));
    }
  };

  const llmModelOptions = useMemo((): Array<LlmModelOption> => {
    if (!dataAI?.length) return [];
    return dataAI.map((m: LlmModel) => ({
      id: m.id.toString(),
      label: m.modelName,
    }));
  }, [dataAI]);

  const selectedAIItem = useMemo(() => {
    if (!llmModelOptions?.length) return "";

    // Handle both 'ai' (string) and 'model' (number) from settings
    const modelId =
      ai || (settings?.model != null ? String(settings.model) : "");
    // Find the matching model in llmModelOptions and return its ID
    const found = modelId
      ? llmModelOptions.find((item) => item.id === modelId)
      : null;
    // Default to first item if nothing is selected
    return found?.id || llmModelOptions[0]?.id || "";
  }, [ai, llmModelOptions, settings]);

  return (
    <Flex direction="column" gap={4} style={{ maxWidth: "25rem" }}>
      <TextInput
        placeholder="Enter button label"
        onChange={handlePlaceholder}
        value={placeholder}
        label="Button label"
      />

      <PromptInput
        prompt={prompt}
        files={files}
        handleFile={handleFile}
        handlePrompt={handlePrompt}
      />

      <SingleSelect
        label="Select AI model"
        placeholder={
          llmModelOptions.length === 0 ? "Loading models..." : "Select AI model"
        }
        data={llmModelOptions}
        selected={selectedAIItem}
        keys={["id", "label"]}
        onChange={handleSelectAI}
        isDisabled={llmModelOptions.length === 0}
      />

      <Label>Add instructions</Label>
      <TextArea
        placeholder="Add additional instructions for the AI field button"
        onChange={(e: FormEvent<HTMLTextAreaElement>) => {
          const value = (e.target as HTMLTextAreaElement)?.value;
          setCurrentSettings((prev) => ({
            ...prev,
            processInstructions: value,
          }));
        }}
        value={processInstructions || ""}
        resize="none"
      />

      <MultiSelect
        label="Select input fields"
        placeholder="Select fields that the AI will use as context"
        data={inputFieldOptions}
        selected={inputFields || []}
        keys={["id", "label"]}
        onChange={(value: Array<FieldId>) => handleSelect("inputFields", value)}
      />

      <MultiSelect
        label="Select output fields"
        placeholder="Select Fields for AI Suggestions"
        data={outputFieldOptions}
        selected={outputFields || []}
        keys={["id", "label"]}
        onChange={(value: Array<FieldId>) =>
          handleSelect("outputFields", value)
        }
      />

      <Flex direction="column" gap={2}>
        <Checkbox
          checked={autoFill}
          onChange={handleToggleAutoFill}
          label="Automatically fill checklist with AI suggestions"
        />
        <Flex gap={1} justify="flex-end">
          <Icon type="info" size="14" style={{ marginTop: "4px" }} />
          <Text variant="caption" color={colors.text.secondary}>
            Enabling this will disable linked record generation in output
            fields.
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Ai;
